import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';

/** localStorage-sleutel waarin de taalkeuze bewaard blijft over pagina's en herladen. */
export const PREFERRED_LANGUAGE_STORAGE_KEY = 'preferredLanguage';

type SupportedLanguage = 'nl' | 'en';

const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ['nl', 'en'];

function isSupportedLanguage(language: string | null): language is SupportedLanguage {
  return language !== null && SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
}

/** Leest de bewaarde taalkeuze uit localStorage; null wanneer er geen geldige keuze bewaard is. */
export function readPreferredLanguage(): SupportedLanguage | null {
  const stored = localStorage.getItem(PREFERRED_LANGUAGE_STORAGE_KEY);
  return isSupportedLanguage(stored) ? stored : null;
}

/** Query-parameter waarmee de landingspagina haar taal meegeeft (https://app.mechanicsinmotion.be/?lang=nl). */
export const LANGUAGE_QUERY_PARAM = 'lang';

/** Leest de taal uit een querystring (`?lang=nl|en`); null zonder geldige waarde. */
export function readLanguageFromSearch(search: string): SupportedLanguage | null {
  const language = new URLSearchParams(search).get(LANGUAGE_QUERY_PARAM);
  return isSupportedLanguage(language) ? language : null;
}

/**
 * Compacte NL | EN taalkeuze voor de publieke account-schermen. Wijzigt de globale
 * Transloco-taal en bewaart de keuze in localStorage zodat ze behouden blijft.
 */
@Component({
  selector: 'language-selector',
  imports: [],
  templateUrl: './language-selector.component.html',
  styleUrl: './language-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageSelectorComponent {
  private readonly transloco = inject(TranslocoService);

  protected readonly languages = SUPPORTED_LANGUAGES;
  protected readonly activeLanguage = signal<SupportedLanguage>(this.currentLanguage());

  protected readonly ariaLabel = computed(() => `Taal / Language: ${this.activeLanguage().toUpperCase()}`);

  protected select(language: SupportedLanguage): void {
    if (language === this.activeLanguage()) {
      return;
    }

    this.transloco.setActiveLang(language);
    localStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, language);
    this.activeLanguage.set(language);
  }

  private currentLanguage(): SupportedLanguage {
    const active = this.transloco.getActiveLang();
    return isSupportedLanguage(active) ? active : 'nl';
  }
}
